import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  bindSingleSession,
  unbind,
  setCurrentSlide,
  setLastSlide,
  setVideoState,
  setTeamName,
  setScore,
  setMissionReportAnswer,
  setHackedAnswer,
  setMode,
  setCurrentLevel,
  completeLevel,
  setConfiguration
} from 'actions/sessionActions';
import {
  showHUD,
  hideHUD,
  registerHUDRef,
  setDeviceRole
} from 'actions/deckActions';
import {
  updateOnboardingState
} from 'actions/profileActions';
import programs from 'programs';
import Spectacle from './spectacle-ppa';

class Presentation extends Component {

  static propTypes = {
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired,
    session: PropTypes.object.isRequired,
    deckState: PropTypes.object.isRequired,
    bindSingleSession: PropTypes.func.isRequired,
    unbind: PropTypes.func.isRequired,
    showHUD: PropTypes.func.isRequired,
    hideHUD: PropTypes.func.isRequired,
    registerHUDRef: PropTypes.func.isRequired,
    setDeviceRole: PropTypes.func.isRequired,
    updateOnboardingState: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired
  };

  static contextTypes = {
    setActiveSession: PropTypes.func.isRequired,
    i18n: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    this.state = {
      displayLoading: false,
      displayTimeoutError: false
    };
  }

  componentDidMount () {
    const sessionId = this.props.params.id;
    this.boundRefs = [];
    this.props.bindSingleSession(sessionId, this.boundRefs);
    dataLayer.push({ event: 'hideChatWidget' });
    setTimeout(() => {
      if (!this.isUnmounted) {
        this.setState({ displayLoading: true });
      }
    }, 2000);
    setTimeout(() => {
      if (!this.isUnmounted) {
        this.setState({ displayTimeoutError: true });
      }
    }, 10000);
  }

  componentWillUnmount () {
    this.props.unbind(this.boundRefs);
    this.isUnmounted = true;
  }

  exitSession () {
    const { setActiveSession } = this.context;
    setActiveSession('none');
  }

  render () {
    const { session, params, dispatch, deckState, auth } = this.props;
    const { deviceRole } = deckState;
    const { displayTimeoutError, displayLoading } = this.state;
    const { id: sessionId } = params;
    const { i18n: _ } = this.context;

    if (!session || !session.programId) {
      // Could be loading or it might not exist
      return (
        <div>
          {(displayLoading && !displayTimeoutError) &&
            <h2>{_('Loading...')}</h2>
          }
          {displayTimeoutError &&
            <div>
              <h2>{_('Unable to load class data')}</h2>
                <button
                    className="btn btn-sm btn-default"
                    onClick={::this.exitSession}>
                  {_('Exit to Programs')}
                </button>
            </div>
          }
        </div>
      );
    }

    const {
      programId,
      currentLevel: sessionCurrentLevel,
      currentSlide: sessionCurrentSlide,
      completedLevel: sessionCompletedLevel,
      configuration: sessionConfiguration,
      contractId,
      funding
    } = session;
    const program = programs.programLookup[programId];
    const { levels, preload } = program;

    const {
      authData: {uid: userId}
    } = auth;

    const boundSetVideoState = bindActionCreators(
      videoState => setVideoState({ sessionId, videoState }),
      dispatch
    );

    const boundSetTeamName = bindActionCreators(
      (team, teamName) => setTeamName({ sessionId, team, teamName }),
      dispatch
    );

    const boundSetScore = bindActionCreators(
      (team, score) => setScore({ sessionId, team, score }),
      dispatch
    );

    const boundSetHackedAnswer = bindActionCreators(
        ({ level, answer }) => setHackedAnswer({
            sessionId,
            level,
            answer
        }),
        dispatch
    );

    const boundSetMissionReportAnswer = bindActionCreators(
      ({ level, questionNumber, question, answer, invalid }) => setMissionReportAnswer({
        sessionId,
        level,
        questionNumber,
        question,
        answer,
        invalid
      }),
      dispatch
    );

    const boundSetMode = bindActionCreators(
      mode => setMode({ sessionId, mode }),
      dispatch
    );

    const boundSetCurrentLevel = bindActionCreators(
      currentLevel => setCurrentLevel({ sessionId, currentLevel }),
      dispatch
    );

    const boundSetCurrentSlide = bindActionCreators(
      currentSlide => setCurrentSlide({ sessionId, currentSlide }),
      dispatch
    );

    const noOp = () => {};
    const isPrepStartSlide = (sessionCurrentLevel === 'prep' && sessionCurrentSlide === 'start');
    const isCurrentLevel = (parseInt(sessionCurrentLevel, 10) === (sessionCompletedLevel || 0) + 1);
    const boundSetLastSlide = isPrepStartSlide || isCurrentLevel
        ? bindActionCreators(
            lastSlide => setLastSlide({ sessionId, lastSlide }),
            dispatch
          )
        : noOp;

    const boundCompleteLevel = bindActionCreators(
      (completedLevel, finished) => completeLevel({ sessionId, completedLevel, programId, finished, contractId, userId, funding }),
      dispatch
    );

    const boundSetConfiguration = bindActionCreators(
      configuration => setConfiguration({ sessionId, configuration }),
      dispatch
    );

    let nextLevel = sessionCompletedLevel ?
        String(Number(sessionCompletedLevel) + 1) : '1';
    if (!levels[nextLevel]) {
      nextLevel = 'completed';
    }

    let deckInterface = null;
    if (sessionCurrentLevel === 'prep') {
      deckInterface = 'classroomDisplay';
    } else {
      if (sessionConfiguration === 'singleScreen') {
        deckInterface = 'classroomDisplay';
      } else if (sessionConfiguration === 'dualScreen') {
        deckInterface = deviceRole;
      }
    }

    if (!deckInterface) {
      deckInterface = 'classroomDisplay';
    }

    return (
      <Spectacle
        {...this.props}
        program={program}
        session={session}
        sessionId={sessionId}
        nextLevel={nextLevel}
        setVideoState={boundSetVideoState}
        setTeamName={boundSetTeamName}
        setScore={boundSetScore}
        setMissionReportAnswer={boundSetMissionReportAnswer}
        setHackedAnswer={boundSetHackedAnswer}
        setMode={boundSetMode}
        setCurrentLevel={boundSetCurrentLevel}
        setCurrentSlide={boundSetCurrentSlide}
        setLastSlide={boundSetLastSlide}
        completeLevel={boundCompleteLevel}
        setConfiguration={boundSetConfiguration}
        deckInterface={deckInterface}
        preload={preload} />
      );
  }

}

export default connect(
  (state, ownProps) => {
    const { deck, auth } = state;
    const { id } = ownProps.params;
    const session = state.sessions.sessions[id];
    return {
      session: {...session},
      deckState: {...deck},
      auth: {...auth}
    };
  },
  (dispatch) => ({
    bindSingleSession: bindActionCreators(bindSingleSession, dispatch),
    unbind: bindActionCreators(unbind, dispatch),
    showHUD: bindActionCreators(showHUD, dispatch),
    hideHUD: bindActionCreators(hideHUD, dispatch),
    setDeviceRole: bindActionCreators(setDeviceRole, dispatch),
    updateOnboardingState: bindActionCreators(updateOnboardingState, dispatch),
    registerHUDRef: bindActionCreators(registerHUDRef, dispatch),
    dispatch
  })
)(Presentation);



// WEBPACK FOOTER //
// ./src/views/session/presentation/index.jsx